// 忘记密码页面 - 现代简约风格
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 验证邮箱格式
    if (!email.trim()) {
      setError(t('forgotPassword.pleaseEnterEmail'));
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError(t('forgotPassword.pleaseEnterValidEmail'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/forgot-password', { email });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('forgotPassword.failedToSendResetEmail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          {!success ? (
            <>
              {/* 标题 */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2">{t('forgotPassword.resetPassword')}</h1>
                <p className="text-white/70 text-sm">{t('forgotPassword.enterEmailForInstructions')}</p>
              </div>

              {/* 错误信息 */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-200 text-sm">
                  {error}
                </div>
              )}

              {/* 重置表单 */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-white/90 text-sm font-medium">{t('forgotPassword.emailAddress')}</label>
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                      placeholder={t('forgotPassword.enterEmailPlaceholder')}
                    />
                  </div>
                </div>

                {/* 发送重置邮件按钮 */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="spinner"></div>
                      {t('forgotPassword.sendingEmail')}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span>{t('forgotPassword.sendResetEmail')}</span>
                    </div>
                  )}
                </button>
              </form>
            </>
          ) : (
            // 成功状态
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">{t('forgotPassword.emailSent')}</h1>
              <p className="text-white/70 text-sm mb-6">
                {t('forgotPassword.checkEmailForInstructions')}
              </p>
            </div>
          )}

          {/* 返回登录链接 */}
          <div className="text-center mt-6">
            <Link 
              to="/login" 
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t('forgotPassword.backToSignIn')}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;